import "swiper/css";

import { ThemeEnum } from "@components/ThemeProvider/ThemeProvider";
import SubHeader from "@src/app/components/SubHeader/SubHeader";
import clsx from "clsx";
import { useMemo, useRef, useState } from "react";
import type { Swiper as SwiperType } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";

type CardsContainerProps<T> = {
  title?: string;
  articles: T[];
  renderItem: (item: T, index: number) => React.ReactNode;
  renderAd?: (index: number) => React.ReactNode;
  theme?: ThemeEnum;
  onPrev?: () => void;
  onNext?: () => void;
  className?: string;
  spaceBetween?: number;
  isSwiper?: boolean;
  showHeader?: boolean;
  noIcon?: boolean;
};

export default function CardsContainer<T extends { id: string }>({
  title = "",
  articles,
  renderItem,
  renderAd,
  theme = ThemeEnum.light,
  onPrev,
  onNext,
  className,
  spaceBetween = 0,
  isSwiper = false,
  showHeader = true,
  noIcon = true,
}: CardsContainerProps<T>) {
  const swiperRef = useRef<SwiperType | null>(null);
  const [isFirstSlide, setIsFirstSlide] = useState(true);
  const [isLastSlide, setIsLastSlide] = useState(false);

  const handlePrev = () => {
    if (isFirstSlide) return;
    swiperRef.current?.slidePrev();
    onPrev?.();
  };

  const handleNext = () => {
    if (isLastSlide) return;
    swiperRef.current?.slideNext();
    onNext?.();
  };

  const isDark = theme === ThemeEnum.dark;

  const sliderSettings = useMemo(
    () => ({
      992: {
        slidesPerGroup: 2,
        slidesPerView: 2.3,
        spaceBetween: 0,
      },
      1200: {
        slidesPerGroup: 3,
        slidesPerView: 3.3,
        spaceBetween: 0,
      },
    }),
    [],
  );

  const handleSlideChange = (swiper: SwiperType) => {
    const currentIndex = swiper.activeIndex;
    const slidesCount = swiper.slides.length;
    const slidesPerView = swiper.params.slidesPerView as number;

    // Calculate the last index that represents the last set of visible slides
    const lastVisibleIndex = slidesCount - slidesPerView;

    // Check if the current slide is the first or last
    setIsFirstSlide(currentIndex === 0);
    setIsLastSlide(currentIndex >= lastVisibleIndex);
  };

  const renderContent = (swiper: boolean) => {
    const elements: React.ReactNode[] = [];
    articles.forEach((item, index) => {
      if (renderAd) {
        const ad = renderAd(index);
        if (ad) {
          elements.push(
            swiper ? (
              <SwiperSlide key={`ad-${index}`} className="h-auto!">
                {ad}
              </SwiperSlide>
            ) : (
              <div key={`ad-${index}`} className="h-full">
                {ad}
              </div>
            ),
          );
        }
      }
      elements.push(
        swiper ? (
          <SwiperSlide key={item.id} className="h-auto!">
            {renderItem(item, index)}
          </SwiperSlide>
        ) : (
          <div key={item.id} className="h-full">
            {renderItem(item, index)}
          </div>
        ),
      );
    });
    return elements;
  };

  return (
    <div className={clsx(isDark && "bg-background-primary-on-dark", className)}>
      <div className="mx-auto w-full max-w-[1200px]">
        {showHeader && (
          <SubHeader
            noIcon={noIcon}
            title={title}
            theme={theme}
            showDivider={true}
            onPrev={isSwiper ? handlePrev : onPrev}
            onNext={isSwiper ? handleNext : onNext}
            disabledPrev={isSwiper ? isFirstSlide : false}
            disabledNext={isSwiper ? isLastSlide : false}
          />
        )}
        {isSwiper ? (
          <div className="pr-3">
            <Swiper
              observer={true}
              observeParents={true}
              slidesPerView={1}
              slidesPerGroup={1}
              spaceBetween={spaceBetween}
              breakpoints={sliderSettings}
              onResize={handleSlideChange}
              onSlideChange={handleSlideChange}
              onSwiper={(swiper) => {
                swiperRef.current = swiper;
                handleSlideChange(swiper);
              }}
              className="w-full"
            >
              {renderContent(true)}
            </Swiper>
          </div>
        ) : (
          <div className="grid w-full grid-cols-1 lg:grid-cols-3">
            {renderContent(false)}
          </div>
        )}
      </div>
    </div>
  );
}
